/**
 * Mux playback por provincia/ciudad (Argentina).
 *
 * Admin: /admin/country-templates (bloque AR geo).
 * DB: site_settings.international_home_mux_by_ar_geo
 *
 * Prioridad de resolución (solo cuando country === AR):
 *   1. ciudad (geo_code / alias del catálogo)
 *   2. provincia (AR_PROVINCES code/name)
 *   3. (caller) país AR en mux_by_country + cadena global
 */

import {
  AR_CABA_ALIASES,
  AR_CITY_CATALOG,
  AR_PROVINCES,
  normalizeCityKey,
} from "@/config/wishlistGeoCatalog";
import { isValidMuxPlaybackId } from "@/lib/internationalHomeTemplate";

export type InternationalHomeMuxByArGeo = {
  provinces: Record<string, string>;
  cities: Record<string, string>;
};

export type InternationalHomeMuxArGeoMatchLevel =
  | "city"
  | "province"
  | "none";

export type ResolveInternationalHomeMuxArGeoResult = {
  playbackId: string | null;
  level: InternationalHomeMuxArGeoMatchLevel;
  /** Clave canónica que matcheó (geo_code o province code). */
  matchedKey: string | null;
};

const EMPTY: InternationalHomeMuxByArGeo = { provinces: {}, cities: {} };

/** Province codes admitidos (exact match sobre AR_PROVINCES[].code). */
const PROVINCE_CODE_SET = new Set(AR_PROVINCES.map((p) => p.code));

/** Mapa normalizeCityKey(code|name) → code canónico. */
const PROVINCE_NORM_TO_CODE = new Map<string, string>();
for (const p of AR_PROVINCES) {
  PROVINCE_NORM_TO_CODE.set(normalizeCityKey(p.code), p.code);
  PROVINCE_NORM_TO_CODE.set(normalizeCityKey(p.name), p.code);
}

/**
 * Variantes de CABA que NO están en AR_PROVINCES pero sí llegan del edge
 * (p. ej. "Buenos Aires F.D.", el `cf.region` real de Cloudflare para la
 * capital). Sin esto `resolveArProvinceCode` devolvía null y el override de
 * video por provincia CABA nunca disparaba para un visitante real.
 *
 * La lista vive en `AR_CABA_ALIASES` (wishlistGeoCatalog) — fuente de verdad
 * única; acá sólo se consume. `scripts/validate-ar-geo-coherence.ts` verifica
 * que las copias de api/ y worker.js no se desincronicen.
 */
for (const variant of AR_CABA_ALIASES) {
  PROVINCE_NORM_TO_CODE.set(normalizeCityKey(variant), "CABA");
}

/** geo_code + aliases → geo_code canónico del catálogo. */
const CITY_ALIAS_TO_CODE = new Map<string, string>();
for (const c of AR_CITY_CATALOG) {
  CITY_ALIAS_TO_CODE.set(normalizeCityKey(c.geo_code), c.geo_code);
  for (const a of c.aliases ?? []) {
    CITY_ALIAS_TO_CODE.set(normalizeCityKey(a.geo_code), c.geo_code);
    if (a.name) CITY_ALIAS_TO_CODE.set(normalizeCityKey(a.name), c.geo_code);
  }
  if (c.name) CITY_ALIAS_TO_CODE.set(normalizeCityKey(c.name), c.geo_code);
}

function pickPlayback(raw: unknown): string | null {
  if (!isValidMuxPlaybackId(raw)) return null;
  return (raw as string).trim();
}

/**
 * Normaliza el mapa AR geo desde admin/API (objeto o JSON string).
 * Descarta claves/playback IDs inválidos.
 */
export function parseInternationalHomeMuxByArGeo(
  raw: unknown,
): InternationalHomeMuxByArGeo {
  let obj: unknown = raw;
  if (typeof raw === "string") {
    if (!raw.trim()) return { ...EMPTY, provinces: {}, cities: {} };
    try {
      obj = JSON.parse(raw);
    } catch {
      return { provinces: {}, cities: {} };
    }
  }
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
    return { provinces: {}, cities: {} };
  }
  const o = obj as Record<string, unknown>;

  const provinces: Record<string, string> = {};
  const rawProv =
    o.provinces && typeof o.provinces === "object" && !Array.isArray(o.provinces)
      ? (o.provinces as Record<string, unknown>)
      : {};
  for (const [k, v] of Object.entries(rawProv)) {
    const key = String(k).trim();
    if (!key) continue;
    // Prefer exact province code; also accept name → code.
    const code =
      PROVINCE_CODE_SET.has(key)
        ? key
        : PROVINCE_NORM_TO_CODE.get(normalizeCityKey(key)) ?? null;
    if (!code) continue;
    const pid = pickPlayback(v);
    if (pid) provinces[code] = pid;
  }

  const cities: Record<string, string> = {};
  const rawCities =
    o.cities && typeof o.cities === "object" && !Array.isArray(o.cities)
      ? (o.cities as Record<string, unknown>)
      : {};
  for (const [k, v] of Object.entries(rawCities)) {
    const key = String(k).trim();
    if (!key) continue;
    const geo =
      CITY_ALIAS_TO_CODE.get(normalizeCityKey(key)) ??
      // Allow free-form geo_code if it looks like a catalog key (lowercase).
      (normalizeCityKey(key) || null);
    if (!geo) continue;
    const pid = pickPlayback(v);
    if (pid) cities[geo] = pid;
  }

  return { provinces, cities };
}

/** Resuelve province string del Worker/CF a code canónico de AR_PROVINCES. */
export function resolveArProvinceCode(
  province: string | null | undefined,
): string | null {
  if (typeof province !== "string" || !province.trim()) return null;
  const raw = province.trim();
  if (PROVINCE_CODE_SET.has(raw)) return raw;
  return PROVINCE_NORM_TO_CODE.get(normalizeCityKey(raw)) ?? null;
}

/** Resuelve city string del Worker/CF a geo_code canónico del catálogo. */
export function resolveArCityGeoCode(
  city: string | null | undefined,
): string | null {
  if (typeof city !== "string" || !city.trim()) return null;
  const norm = normalizeCityKey(city);
  if (!norm) return null;
  return CITY_ALIAS_TO_CODE.get(norm) ?? norm;
}

/**
 * Playback ID para visitante AR por ciudad/provincia.
 * No aplica si country no es AR (caller debe filtrar).
 */
export function resolveInternationalHomeMuxForArGeo(opts: {
  province?: string | null;
  city?: string | null;
  muxByArGeo?: InternationalHomeMuxByArGeo | null;
}): ResolveInternationalHomeMuxArGeoResult {
  const map = opts.muxByArGeo;
  if (!map) {
    return { playbackId: null, level: "none", matchedKey: null };
  }

  const cityKey = resolveArCityGeoCode(opts.city);
  if (cityKey && map.cities[cityKey] && isValidMuxPlaybackId(map.cities[cityKey])) {
    return {
      playbackId: map.cities[cityKey].trim(),
      level: "city",
      matchedKey: cityKey,
    };
  }

  const provinceCode = resolveArProvinceCode(opts.province);
  if (
    provinceCode &&
    map.provinces[provinceCode] &&
    isValidMuxPlaybackId(map.provinces[provinceCode])
  ) {
    return {
      playbackId: map.provinces[provinceCode].trim(),
      level: "province",
      matchedKey: provinceCode,
    };
  }

  return { playbackId: null, level: "none", matchedKey: null };
}

/** true si hay al menos un override de ciudad o provincia. */
export function hasArGeoMuxOverrides(
  map: InternationalHomeMuxByArGeo | null | undefined,
): boolean {
  if (!map) return false;
  return (
    Object.keys(map.provinces).some((k) => isValidMuxPlaybackId(map.provinces[k])) ||
    Object.keys(map.cities).some((k) => isValidMuxPlaybackId(map.cities[k]))
  );
}

export function countArGeoMuxOverrides(
  map: InternationalHomeMuxByArGeo | null | undefined,
): { provinces: number; cities: number } {
  if (!map) return { provinces: 0, cities: 0 };
  return {
    provinces: Object.values(map.provinces).filter(isValidMuxPlaybackId).length,
    cities: Object.values(map.cities).filter(isValidMuxPlaybackId).length,
  };
}
